import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { Popover } from "@msuite/ui/popover";
import { invalidate, type RefetchFn, useQuery, useQueryClient } from "@msuite/ui/query";
import { RowActionsWrapper } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import type { Nullable } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { ContactPopover } from "@/components/popovers/contact-popover";
import { useCitadel } from "@/hooks/use-citadel";
import { useProject } from "@/hooks/use-project";
import { client } from "@/hono";
import { EditContactModal } from "@/modals/contacts/edit-contact-modal";
import { AddProjectContactModal } from "@/modals/projects/add-project-contact-modal";

/** Props Interface */
interface ProjectContactHeaderProps {
	contactId: Nullable<string>;
	projectId: string;
	refetch: RefetchFn;
	numberOfItems: number;
}

export function ProjectContactHeader({
	contactId,
	projectId,
	refetch,
	numberOfItems,
}: ProjectContactHeaderProps) {
	/** Query */
	const { data: projectContact, refetch: projectContactRefetch } = useQuery({
		queryKey: ["project-contact", projectId, contactId || ""],
		queryFn: async () => {
			if (!contactId) return null;
			const response = await client.projects[":projectId"].contacts[":contactId"].$get({
				param: { projectId, contactId },
			});
			const json = await responseHandler(response);
			if (!json.contact) throw new Error("Contact not found");
			return json.contact;
		},
	});

	const { citadel } = useCitadel();
	const { project } = useProject({ projectId });

	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const asyncModal = useAsyncModal();
	const resetAsyncModal = useAsyncModal();
	const qc = useQueryClient();
	const editContactModal = useDisclosure();
	const editContactOverrideModal = useDisclosure();
	const addProjectContactModal = useDisclosure();

	/** Functions */
	function handleOnNavigateToContact() {
		if (!contactId) return;
		navigate(`/contacts/${contactId}`);
	}

	async function handleOnResetContactOverride() {
		try {
			const isConfirmed = await resetAsyncModal.confirm();
			if (!isConfirmed) return;
			if (!contactId) return;
			const response = await client.projects[":projectId"].contacts[":contactId"].override.$delete({
				param: { projectId, contactId },
			});
			await responseHandler(response);
			await refetch?.();
			await projectContactRefetch?.();
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnRemoveContactFromProject() {
		try {
			if (!contactId) return null;
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await client.projects[":projectId"].contacts[":contactId"].$delete({
				param: {
					projectId,
					contactId,
				},
			});
			await qc.invalidateQueries({
				predicate: invalidate([projectId]),
			});
			await refetch?.();
		} catch (error) {
			console.error(error);
		}
	}

	if (!contactId) return <p>{guardString(null)}</p>;

	/** Computed */
	// Every write in this menu goes to a `/projects/:projectId/...` route, so what governs them is
	// changing the site rather than changing the address book — except the edit, which saves the
	// contact itself as well and therefore needs both rules. The project rule binds against the
	// site, because `update Project` reaches the organisation, the sites its holder runs or the
	// ones they created. Same three-state guard as `Can` documents: an entry disappears on a
	// definitive denial only, so a rule list or a project still loading keeps it.
	const projectWriteIsDenied =
		!!citadel && !!project && citadel.cannot("update", "Project", { ...project });
	const contactWriteIsDenied = !!citadel && citadel.cannot("update", "Contact");
	const editIsDenied = projectWriteIsDenied || contactWriteIsDenied;

	const contactOverrides = projectContact?._contactOverride?.overrides as Record<
		string,
		unknown
	> | null;
	const contactDisplayName = (contactOverrides?.name as string | null) ?? projectContact?.name;

	/** Render */
	return (
		<React.Fragment>
			<Popover
				trigger="hover"
				render={() => <ContactPopover clientId={contactId} />}
			>
				<div className="h-stack justify-between">
					<Tag
						variant="plain"
						color={projectContact?._type?.color || "gray"}
					>
						<div className="h-stack gap-4">
							{contactDisplayName} ({numberOfItems})
							{projectContact?._type?.name && (
								<span className="label inline-flex">
									{projectContact?._type?.name || "Unknown"}
								</span>
							)}
						</div>
					</Tag>
					<div onClick={(e) => e.stopPropagation()}>
						<RowActionsWrapper to={`/contacts/${contactId}`}>
							<Menu
								items={createStandardMenuItems({
									onOpen: handleOnNavigateToContact,
									onEdit: editIsDenied ? undefined : editContactModal.onOpen,
									onDelete: projectWriteIsDenied ? undefined : handleOnRemoveContactFromProject,
									items: projectWriteIsDenied
										? []
										: [
												{
													label: t("contacts.add_member"),
													icon: icon("UserPlus"),
													onClick: addProjectContactModal.onOpen,
													cyId: "row-actions-manage-members-button",
												},
												{
													label: t("projects.edit_contact_project_override"),
													icon: icon("Pencil"),
													onClick: editContactOverrideModal.onOpen,
												},
												{
													label: t("projects.reset_contact_project_override"),
													icon: icon("RotateCcw"),
													onClick: handleOnResetContactOverride,
												},
											],
								})}
							>
								<Button
									variant="ghost"
									icon={<Icon name="EllipsisVertical" />}
									stopPropagation
								/>
							</Menu>
						</RowActionsWrapper>
					</div>
				</div>
			</Popover>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("projects.remove_contact_from_project_confirm_message")}
				/>
			)}
			{resetAsyncModal.isOpen && (
				<ConfirmModal
					controller={resetAsyncModal}
					message={t("projects.reset_contact_project_override_confirmation")}
				/>
			)}
			{editContactModal.isOpen && (
				<EditContactModal
					isOpen={editContactModal.isOpen}
					onClose={editContactModal.onClose}
					contactId={contactId}
					onComplete={async () => {
						await refetch?.();
						await projectContactRefetch?.();
					}}
				/>
			)}
			{editContactOverrideModal.isOpen && (
				<EditContactModal
					isOpen={editContactOverrideModal.isOpen}
					onClose={editContactOverrideModal.onClose}
					contactId={contactId}
					projectOverride={{ projectId }}
					onComplete={async () => {
						await refetch?.();
						await projectContactRefetch?.();
					}}
				/>
			)}
			{addProjectContactModal.isOpen && (
				<AddProjectContactModal
					projectId={projectId}
					contactId={contactId}
					isOpen={addProjectContactModal.isOpen}
					onClose={addProjectContactModal.onClose}
					onComplete={async () => {
						await refetch?.();
						await projectContactRefetch?.();
					}}
				/>
			)}
		</React.Fragment>
	);
}
